import { Flex, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStepId, getTrackingOp, setTrackingOp, useTrackingOp } from "../../../../contexts/TrackingOpContext";
import { datetime } from "../../../../util/library/datetime";
import { EXECUTION_TYPE, fishHealth } from "../../../../util/varibles/constants";
import { convertStepName, durationTxt, formatNumber, getElById } from "../../../../util/varibles/global";
import { IActivityLog, IRoundPlan, ITaskTreatment, ITreatment2, IUnit, TProcessItem } from "../../../../util/varibles/interface";
import Icon, { ICON_NAME } from "../../../Icon";
import Density from "../../../Operation/Density2";
import UnitDisease from "../../../Operation/FishDisease/UnitDisease";
import TaskView from "../../../Operation/Task/TaskView";
import FinishStepButton from "../FinishStepButton";
import FishUnitInfo from "../FishUnitInfo";
import LiveWeather from "../LiveWeather";
import NextTabButton from "../NextTabButton";
import stylesParent from "../style.module.scss";
import styles from "./style.module.scss";

const NextTab = ({ isDone }: { isDone: boolean }) => {
    const set = setTrackingOp();

    return <>
        {isDone && <NextTabButton
            name={"Finish treating"}
            onClick={() => {
                set(({ step }) => ({ step: step + 1 }))
            }}
        />}
    </>
}

const ScrollVertical = ({ children, state }: { state?: TProcessItemExtand, children: React.ReactNode }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const init = useRef(true);

    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    useEffect(() => {
        let mode: { behavior?: ScrollBehavior } = { behavior: "smooth" };
        let timer: ReturnType<typeof setTimeout> | undefined;
        const isInitialRender = init.current;

        if (isInitialRender) {
            init.current = false;
            mode = {};
        }
        if (state) {
            timer = setTimeout(() => {
                const el = getElById(`round-${state?.round}`);
                if (el) {
                    el.scrollIntoView(mode);
                }
            }, 300)
        } else if (isInitialRender) {
            const el = rootRef.current;
            if (el) {
                el.scrollTo({ ...mode, left: 0 });
            }
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [state])

    return <div ref={rootRef} className={styles.rounds} >
        {children}
    </div>;
}

const getFinishTime = (value: TProcessItemExtand) => {
    const { nextStartTime } = value;
    if (!nextStartTime)
        return "--";
    return datetime(nextStartTime).format("HH:mm")
}

interface IExtandTanks {
    unit_id: string,
    loading?: TProcessItemExtand & { real_finish_time?: number },
    tank?: TProcessItemExtand & { real_finish_time?: number }
    tasks: (TProcessItemExtand & { real_finish_time?: number })[],
    state?: number
}

type TGroups = { [unitId: string]: { [roundId: string]: IRoundPlan & { newTanks: IExtandTanks[], state?: number, done?: boolean } } };
type TProcessItemExtand = TProcessItem & { nextStartTime?: number };

export default function TreatingStep({ log, stepIndex }: { log: IActivityLog, stepIndex: number }) {
    const stepId = useRef(getStepId(log, stepIndex));
    const [op] = useTrackingOp(state => state.op?.operation as ITreatment2);
    const { round_map = {}, sub_operations, tasks, execution_type } = op || {};
    const unitStates = useRef<{ [unitId: string]: { count: number, round?: string, state: number, isStart?: boolean, isDone?: boolean, isRunning?: boolean } }>({});
    const set = setTrackingOp();
    const get = getTrackingOp();
    const [isStepDone, setIsStepDone] = useState(false);
    const [groups, setGroups] = useState<TGroups>({});

    const handleClick = useCallback((unitId: string) => {
        setUnit(sub_operations?.find(item => item.unit_id === unitId));
    }, [sub_operations]);

    const [unit, setUnit] = useState<IUnit>();
    const currentTasks = useMemo<ITaskTreatment[]>(() => {
        if (!unit) return [];
        return tasks?.filter(item => item.sub_operation.unit_id === unit.unit_id) || [];
    }, [tasks, unit]);

    useEffect(() => {
        unitStates.current = {};
        const roundIds = Object.keys(round_map);
        const result = roundIds.reduce((rs: TGroups, roundId: string, roundIndex: number) => {
            const { tanks = [] } = round_map[roundId];
            const nextRound = round_map[roundIds[roundIndex + 1]];
            const nextRoundStart = nextRound?.tanks
                ?.flatMap(item => item.processes || [])
                .find(item => item.real_start_time)
                ?.real_start_time;
            const referenceProcesses = tanks.find(item => item.processes?.length)?.processes || [];
            const finishTimes = referenceProcesses.map((_, processIndex) =>
                referenceProcesses[processIndex + 1]?.real_start_time || nextRoundStart
            );
            const convertValue = (item: TProcessItem, processIndex: number) => ({
                ...item,
                nextStartTime: finishTimes[processIndex]
            });
            let ids = new Set<string>([]);

            const newTanks = tanks.map((item: IRoundPlan['tanks'][0]) => {
                const { processes = [] } = item;
                const { unit_id } = processes[0] || {};
                ids.add(unit_id || '');
                let index = -1, loading: TProcessItem | undefined, tank: TProcessItem | undefined, tasks: TProcessItem[] = [];
                if (processes[0]) {
                    const first = convertValue(processes[0], 0);
                    if (first.keys?.includes('loading')) {
                        loading = first;
                    } else if (first.type === 'tank') {
                        tank = first;
                    } else {
                        tasks.push(first);
                    }
                    if (first.real_start_time) {
                        index = 0;
                    }
                }
                for (let i = 1; i < processes.length; i++) {
                    const item = convertValue(processes[i], i);
                    if (item.type === 'tank') {
                        tank = item;
                    } else {
                        tasks.push(item);
                    }
                    if (item.real_start_time) {
                        index++;
                    }
                }

                return { unit_id, loading, tank, tasks, state: index, done: index !== -1 && index === processes.length - 1 };
            });
            ids.delete('');
            ids.forEach(id => {
                rs[id] = rs[id] || {};
                const { state = -1 } = newTanks.find(item => item.unit_id === id) || {};
                const done = newTanks.filter(item => item.unit_id === id).every(item => item.done);

                if (done) {
                    unitStates.current[id] = {
                        count: (unitStates.current[id]?.count || 0) + 1,
                        round: roundId,
                        state,
                        isRunning: false
                    }
                } else if (!unitStates.current[id]?.isRunning) {
                    const { count = 0 } = unitStates.current[id] || {};
                    unitStates.current[id] = { count, round: roundId, state, isRunning: true };
                }
                rs[id][roundId] = { ...round_map[roundId], newTanks, state, done };
            });

            return rs;
        }, {});
        const unitIds = Object.keys(unitStates.current);
        let done = unitIds.length > 0;
        unitIds.forEach(key => {
            unitStates.current[key].isStart = (unitStates.current[key].count + unitStates.current[key].state) > -1;
            if (unitStates.current[key].count === Object.keys(result[key] || {}).length) {
                const { round } = unitStates.current[key];
                unitStates.current[key].isDone = !!result[key]?.[round || ""]?.done;
            } else
                unitStates.current[key].isDone = false;
            done = done && !!unitStates.current[key].isDone;
        })

        setIsStepDone(done);
        set(({ stepStates: prev }) => {
            const nextState = done ? 1 : 0;
            if (prev[stepId.current]?.state === nextState) return {};
            return { stepStates: { ...prev, [stepId.current]: { state: nextState } } };
        });
        setGroups(result);
    }, [round_map, set])

    useEffect(() => {
        const unitIds = Object.keys(groups);
        if (unit?.unit_id && unitIds.includes(unit.unit_id)) return;

        const firstUnitId = unitIds[0];
        setUnit(firstUnitId
            ? sub_operations?.find(item => item.unit_id === firstUnitId)
            : undefined);
    }, [groups, sub_operations, unit?.unit_id]);

    const { unitRounds, state } = useMemo(() => {
        const unitId = unit?.unit_id;
        let state: TProcessItem & { round_name: string } | undefined;
        if (!unitId)
            return { unitRounds: {} as typeof plannedUnitRounds, state };
        const plannedUnitRounds = groups[unitId] || {};
        const { isDone = false, isStart = false, round, state: roundState = -1 } = unitStates.current[unitId] || {};
        if (!isDone) {
            const { tanks = [] } = plannedUnitRounds[round || ''] || {};
            const { processes = [] } = tanks.find(item => item.processes?.[0]?.unit_id === unitId) || {};
            const value = { ...processes[roundState + 1], round_name: round || '' };
            if (isStart) {
                state = value;
            } else {
                const { [unitId]: _, ...args } = unitStates.current || {};
                const runningKey = Object.keys(args).find(key => args[key].isStart && !args[key].isDone);
                if (!runningKey) {
                    state = value
                }
            }
        }
        return { unitRounds: plannedUnitRounds, state }
    }, [groups, unit])

    if (!op) return <></>;

    return <>
        <div className="w-full">
            <div className="mb-1">
                <LiveWeather vesselId={get().op?.vessel.id || ""} />
            </div>
            <div className={stylesParent.units}>
                {Object.keys(groups).map(key => {
                    const rounds = Object.keys(groups[key] || {}).length;
                    return <div key={key} className={stylesParent.unit} data-active={unit?.unit_id === key} onClick={() => handleClick(key)}>
                        <div className="font-w4">{key}</div>
                        <div>{unitStates.current[key]?.isDone ? <Icon icon={ICON_NAME.CHECKED} bg="colorActive" /> : `${rounds} Round${rounds !== 1 ? 's' : ''}`}</div>
                    </div>
                })}
            </div>
            <Space orientation="vertical" size={1} >
                <FishUnitInfo unit={unit} />
                <Flex gap={12}>
                    <Flex gap={6}>
                        <Typography.Text type="secondary">Unit diseases: </Typography.Text>
                        <Typography.Text><UnitDisease unit={unit} /></Typography.Text>
                    </Flex>
                    <Flex gap={6}>
                        <Typography.Text type="secondary">Fish health: </Typography.Text>
                        <Typography.Text>
                            <>{fishHealth[unit?.fish_health || ""]?.name || "-"}</>
                        </Typography.Text>
                    </Flex>
                </Flex>
                <div className="my-2">
                    <TaskView tasks={currentTasks} />
                </div>
                <Flex gap={6} className="mt-2">
                    <Typography.Text type="secondary">Mortality count: </Typography.Text>
                    <Typography.Text>
                        <>{unit?.mortality_risk || "-"}</>
                    </Typography.Text>
                </Flex>
            </Space>
            <ScrollVertical state={state}>
                {Object.keys(unitRounds).map((key) => {
                    const { tanks = [], newTanks } = unitRounds[key] || {};
                    return <div key={key} id={`round-${key}`} className={styles.round} >
                        <div className="font-w5">Round {key}</div>
                        <div className={stylesParent.tanks} style={{ '--tank-count': tanks.length } as any}>
                            {tanks.map((item, i) => {
                                const { loading, tank, tasks = [] } = newTanks[i] || {};
                                const { keys = [], duration = 0 } = tank || {};

                                return <div className={stylesParent.tank} key={item.tank}>
                                    <div className="flex justify-end">
                                        {loading?.real_start_time ? <div className="flex gap-1 text-[14px] leading-[23px]">
                                            <span className="text-comment">{datetime(loading?.real_start_time).format("HH:mm")}</span>
                                            <span>Loading</span>
                                            <span className="text-comment">{getFinishTime(loading)}</span>
                                        </div> : ``}
                                    </div>
                                    <div className="!justify-between px-3" data-lev="in-tank">
                                        <span>{item.total_weight ? `${formatNumber(Math.round(item.total_weight / 10000) / 100)}t` : null}</span>
                                        <span>{keys.length > 0 ? <>{convertStepName(keys).join(', ')}: {durationTxt(duration)}</> : '--'}</span>
                                    </div>
                                    <div className="!justify-between pr-3" data-lev="tank-name">
                                        <div className="text-comment font-w5 text-[14px]">{item.tank}</div>
                                        <Density
                                            density={(tank as TProcessItem & { density?: number })?.density || item.density || 0}
                                            max_density={item.max_density}
                                        />
                                    </div>
                                    <div className="flex flex-col items-end mt-[5px]">
                                        {tasks.map(sub => sub.real_start_time ? <div className="flex gap-1 text-[14px] leading-[23px]" key={sub.real_start_time}>
                                            <span className="text-comment">{datetime(sub.real_start_time).format("HH:mm")}</span>
                                            <span>{convertStepName(sub?.keys || []).join(', ')}</span>
                                            <span className="text-comment">{getFinishTime(sub)}</span>
                                        </div> : null)}
                                    </div>
                                </div>
                            })}
                        </div>
                    </div>
                })}
            </ScrollVertical>
        </div>
        {execution_type !== EXECUTION_TYPE.AUTO && <FinishStepButton
            name={state ? `${convertStepName(state?.keys || []).join(', ')} round ${state.round_name || 1}` : undefined}
            payload={state
                ? {
                    unit_id: state.unit_id,
                    round_no: state.round,
                    keys: state.keys,
                    index: state.index
                }
                : undefined}
        />}
        <NextTab isDone={isStepDone || execution_type === EXECUTION_TYPE.AUTO} />
    </>
}
